
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { ChangeEvent, useRef } from "react";
import { Upload } from "lucide-react";

export interface UserAvatarProps {
  src: string | null;
  name: string;
  size?: "small" | "medium" | "large";
  className?: string;
  onImageUpload?: (file: File) => void;
  editable?: boolean;
}

export function UserAvatar({ 
  src, 
  name, 
  size = "medium", 
  className,
  onImageUpload,
  editable = false 
}: UserAvatarProps) {
  // Get initials from name
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  // Set size classes
  const sizeClasses = {
    small: "h-8 w-8 text-xs",
    medium: "h-10 w-10 text-sm",
    large: "h-24 w-24 text-xl",
  };

  const inputRef = useRef<HTMLInputElement>(null);
  
  const handleClick = () => {
    if (editable && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onImageUpload) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }
      onImageUpload(file);
    }
  };

  return (
    <div className={cn("relative", editable && "cursor-pointer", className)} onClick={handleClick}>
      <Avatar className={cn(sizeClasses[size])}>
        <AvatarImage src={src || ""} alt={name} />
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      
      {editable && (
        <>
          <div className="absolute inset-0 bg-black/30 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
            <Upload className="h-6 w-6 text-white" />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
            aria-label="Upload profile picture"
          />
        </>
      )}
    </div>
  );
}
